/**
 * Integration Commands - General integration connection and management
 * 
 * These commands handle connecting various integrations (Plaid, Gmail, etc.)
 * in a unified way.
 */

import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { createFileLogger } from '../../utils/logger';
import { API_BASE_URL } from '../../api/config';

const log = createFileLogger('INTEGRATION_COMMANDS');

/**
 * Setup Integration Tool - Universal Integration Setup
 * 
 * Shows a setup button in chat for missing integrations.
 * Handles both API connections (Stripe, Mixpanel) and OAuth integrations (Plaid, Gmail).
 * 
 * The tool shows a button that:
 * - For API connections: Opens modal for entering API keys
 * - For OAuth integrations: Triggers OAuth popup flow
 */
export const setupIntegrationCommand: SelfContainedCommand = {
  id: 'setup_integration',
  description: `🔌 SETUP INTEGRATION: Set up missing integrations with one click

**PURPOSE:**
Shows a setup button in chat when an integration is needed but not available.
Handles the entire setup flow for both API and OAuth integrations.

**WHEN TO USE:**
Use this tool when you detect a missing integration:
- Bank transactions (Plaid Banking)
- Email access (Gmail)
- Calendar (Google Calendar)
- Analytics (Google Analytics)
- API connections (Stripe, Mixpanel, Apollo, etc.)

**IMPORTANT:**
- ✅ Call setup_integration when you detect a missing integration
- ❌ Do NOT use entity_create to create Integration/APIConnection entities
- ❌ Do NOT try to connect integrations yourself with other tools

**HOW IT WORKS:**
1. You call setup_integration → Shows setup button in chat
2. ⚠️ Agentic flow PAUSES (autonomyLevel: 'ask') - wait for user approval
3. User clicks button → For API: Modal opens / For OAuth: OAuth popup opens
4. User enters credentials/authorizes → Connection created
5. User approves the tool or sends new message
6. You can then use the integration (api_connect, data_query, etc.)

**IMPORTANT:**
- This tool has autonomyLevel: 'ask' which pauses the agentic flow
- Wait for user to complete setup before proceeding
- Don't loop - if integration is missing, call this ONCE then wait

**EXAMPLES:**

Missing Stripe API:
\`\`\`json
{
  "integrationType": "api",
  "serviceName": "Stripe",
  "purpose": "To create products and prices in Stripe for your e-commerce store"
}
\`\`\`

Missing Plaid Banking:
\`\`\`json
{
  "integrationType": "oauth",
  "serviceName": "Plaid",
  "purpose": "To query bank transaction data"
}
\`\`\`

Missing Gmail:
\`\`\`json
{
  "integrationType": "oauth",
  "serviceName": "Gmail",
  "purpose": "To read and search emails"
}
\`\`\`

**INTEGRATION TYPES:**
- "api" - API connections (Stripe, Mixpanel, etc.) - require API keys
- "oauth" - OAuth integrations (Gmail, Plaid, Google services) - require OAuth flow

**AFTER SETUP:**
Once the user sets up the integration, you can use the normal tools:
- api_connect (for API connections)
- data_query (for bank transactions)
- email_read (for emails)
- calendar_read (for calendar)
- etc.`,

  autonomyLevel: 'ask',  // ✅ PAUSE FLOW: Wait for user to complete setup
  visualTreatment: 'demonstrative',  // Show prominently as it requires user action
  
  schema: {
    parameters: [
      {
        name: 'integrationType',
        type: 'string',
        description: 'Type of integration: "api" (API key-based) or "oauth" (OAuth-based)',
        required: true,
        validator: (value: string) => {
          if (!['api', 'oauth'].includes(value.toLowerCase())) {
            return 'Integration type must be "api" or "oauth"';
          }
          return null;
        }
      },
      {
        name: 'serviceName',
        type: 'string',
        description: 'Name of the service (e.g., "Stripe", "Plaid", "Gmail", "Mixpanel")',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'Service name is required';
          }
          return null;
        }
      },
      {
        name: 'purpose',
        type: 'string',
        description: 'Brief explanation of why this integration is needed for the current task',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) {
            return 'Purpose is required';
          }
          return null;
        }
      },
      {
        name: 'contextEntity',
        type: 'string',
        description: 'Entity ID to automatically link integration to (e.g., current app_project or website_project ID). Creates entity_link automatically after connection.',
        required: false
      },
      {
        name: 'autoLinkRelation',
        type: 'string',
        description: 'Relationship type for auto-linking (default: "uses_api_connection"). Only used if contextEntity is provided.',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: {
          integrationType: 'api',
          serviceName: 'Stripe',
          purpose: 'To create products and prices in Stripe'
        },
        expectedOutput: createSuccessResult('Stripe integration needs to be set up', { needsSetup: true }),
        description: 'Prompt user to set up Stripe API connection'
      }
    ],
    bad: []
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { integrationType, serviceName, purpose, contextEntity, autoLinkRelation } = params;
    
    log.log('[setup_integration] Called with:', { integrationType, serviceName, purpose, contextEntity });
    
    // This tool doesn't actually do anything - the renderer handles showing the setup button
    // We just return success with metadata that the renderer will use
    // The renderer will handle auto-linking if contextEntity is provided
    return createSuccessResult(
      `${serviceName} integration needs to be set up`,
      {
        integrationType,
        serviceName,
        purpose,
        needsSetup: true,
        setupReady: true,
        contextEntity,
        autoLinkRelation: autoLinkRelation || 'uses_api_connection'
      },
      {
        format: 'json'
      }
    );
  }
};


/**
 * Connect Integration Command - Internal OAuth Handler
 * 
 * ⚠️ INTERNAL USE ONLY - Not exposed to AI
 * 
 * This tool performs the actual OAuth connection flow.
 * It's called internally by the setup_integration button for OAuth integrations.
 * AI should always use setup_integration instead.
 */
export const connectIntegrationCommand: SelfContainedCommand = {
  id: 'connect_integration',
  description: `⚠️ INTERNAL TOOL - Use setup_integration instead

Connect an external OAuth integration to River.

This tool is called internally by the setup_integration button.
AI should not call this directly - use setup_integration to show the setup button.

**PURPOSE:**
Performs OAuth flow for integrations (banks, email, calendar, etc.).

**SUPPORTED INTEGRATIONS:**
- **plaid** - Bank account connection (Plaid Banking)
- **gmail** - Email access (Gmail)
- **google_calendar** - Calendar events (Google Calendar)
- **google_drive** - File storage (Google Drive)
- **dropbox** - File storage (Dropbox)

**EXAMPLES:**

Connect Plaid (banking):
{
  "integration": "plaid",
  "autoSync": true
}

Connect Gmail:
{
  "integration": "gmail"
}

**HOW IT WORKS:**
1. Opens OAuth popup for the service
2. User authorizes access
3. Stores encrypted credentials
4. Optionally syncs initial data
5. Makes data available via entities

**WHAT HAPPENS NEXT:**
After connection, data becomes available:
- Bank transactions → \`BankTransaction\` entities
- Emails → \`Email.Gmail\` entities (optional)
- Calendar events → \`CalendarEvent\` entities
- Files → \`File\` entities

Apps can then query this data using \`river.entity.query()\`.`,
  
  autonomyLevel: 'ask', // Always ask user before OAuth
  visualTreatment: 'narrative',
  
  schema: {
    parameters: [
      {
        name: 'integration',
        type: 'string',
        description: 'Integration type: plaid | gmail | google_calendar | google_drive | dropbox',
        required: true,
        examples: ['plaid', 'gmail', 'google_calendar'],
        validator: (value: string) => {
          const valid = ['plaid', 'gmail', 'google_calendar', 'google_drive', 'dropbox'];
          if (!valid.includes(value.toLowerCase())) {
            return `Invalid integration. Must be one of: ${valid.join(', ')}`;
          }
          return null;
        }
      },
      {
        name: 'autoSync',
        type: 'boolean',
        description: 'Whether to automatically sync data after connection (default: true)',
        required: false,
        defaultValue: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { integration: 'plaid', autoSync: true },
        expectedOutput: createSuccessResult('Plaid connected', {
          integration: 'plaid',
          status: 'connected',
          dataAvailable: true
        }),
        description: 'Connect Plaid banking',
        context: 'User wants to access bank transaction data'
      },
      {
        input: { integration: 'gmail' },
        expectedOutput: createSuccessResult('Gmail connected', {
          integration: 'gmail',
          status: 'connected'
        }),
        description: 'Connect Gmail',
        context: 'User wants to access email data'
      }
    ],
    bad: [
      {
        input: { integration: 'invalid' },
        error: 'Invalid integration',
        why: 'Integration type not supported',
        fix: 'Use a supported integration type'
      }
    ]
  },
  
  performance: {
    expectedDuration: 30000, // OAuth can take up to 30 seconds
    complexity: 'high'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { integration, autoSync = true } = params;
    
    const integrationType = integration.toLowerCase();
    
    try {
      log.info('[connect_integration] Starting connection:', { integrationType, autoSync });
      
      // Route to specific integration handler
      switch (integrationType) {
        case 'plaid':
          return await connectPlaid(context, autoSync);
        
        case 'gmail':
          return await connectGmail(context);
        
        case 'google_calendar':
          return await connectGoogleCalendar(context);
        
        case 'google_drive':
          return await connectGoogleDrive(context);
        
        case 'dropbox':
          return await connectDropbox(context);
        
        default:
          return createErrorResult(
            `Integration not yet implemented: ${integration}`,
            `The ${integration} integration is planned but not yet available.`,
            'validation'
          );
      }
      
    } catch (error: any) {
      log.error('[connect_integration] Error:', error);
      return createErrorResult(
        'Integration connection failed',
        error.message,
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

/**
 * Plaid Banking Connection
 * 
 * Note: This function returns a Promise that resolves when user completes OAuth.
 * ToolExecutor handles the timeout (5 minutes) - this gives users time to:
 * 1. Read the Plaid popup
 * 2. Search for their bank
 * 3. Enter credentials
 * 4. Wait for authorization
 * 5. Return to River
 */
async function connectPlaid(context: CommandExecutionContext, autoSync: boolean): Promise<StandardToolResult> {
  log.info('[connectPlaid] Starting Plaid connection (5 minute timeout handled by ToolExecutor)');
  
  try {
    // Step 1: Create link token
    const authToken = localStorage.getItem('authToken');
    const linkTokenUrl = `${API_BASE_URL}/plaid/create_link_token`;
    
    log.info('[connectPlaid] Creating link token:', { url: linkTokenUrl });
    
    const linkTokenResponse = await fetch(linkTokenUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        client_name: 'River',
        language: 'en',
        country_codes: ['US'],
        products: ['transactions']
      })
    });
    
    if (!linkTokenResponse.ok) {
      const errorText = await linkTokenResponse.text();
      log.error('[connectPlaid] Link token request failed:', { 
        status: linkTokenResponse.status, 
        statusText: linkTokenResponse.statusText,
        body: errorText
      });
      throw new Error(`Failed to create Plaid link token: ${linkTokenResponse.status} ${linkTokenResponse.statusText}`);
    }
    
    const { link_token } = await linkTokenResponse.json();
    
    log.info('[connectPlaid] Link token created, opening Plaid Link');
    
    // Step 2: Open Plaid Link in popup
    // Note: ToolExecutor handles timeout (5 minutes for OAuth flows)
    return new Promise((resolve, reject) => {
      // Import Plaid dynamically
      const script = document.createElement('script');
      script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
      
      script.onerror = () => {
        log.error('[connectPlaid] Failed to load Plaid script');
        resolve(createErrorResult(
          'Failed to load Plaid',
          'Could not load Plaid Link script. Please check your internet connection.',
          'execution'
        ));
      };
      
      script.onload = () => {
        const plaid = (window as any).Plaid;
        
        if (!plaid) {
          log.error('[connectPlaid] Plaid not available after script load');
          resolve(createErrorResult(
            'Plaid not available',
            'Plaid Link script loaded but Plaid object not found.',
            'execution'
          ));
          return;
        }
        
        const linkHandler = plaid.create({
          token: link_token,
          onSuccess: async (public_token: string, metadata: any) => {
            log.info('[connectPlaid] User authorized, exchanging token');
            
            try {
              // Step 3: Exchange public token
              const exchangeUrl = `${API_BASE_URL}/plaid/exchange_public_token`;
              log.info('[connectPlaid] Exchanging token:', { url: exchangeUrl });
              
              // Format accounts to match backend schema (Dict[str, str])
              // Plaid returns accounts with various fields, we need only string values
              const formattedAccounts = (metadata.accounts || []).map((acc: any) => {
                const formatted: Record<string, string> = {};
                
                // Only include string-convertible fields
                if (acc.id) formatted.id = String(acc.id);
                if (acc.name) formatted.name = String(acc.name);
                if (acc.type) formatted.type = String(acc.type);
                if (acc.subtype) formatted.subtype = String(acc.subtype);
                if (acc.mask) formatted.mask = String(acc.mask);
                
                // Add verification_status if present
                if (acc.verification_status) {
                  formatted.verification_status = String(acc.verification_status);
                }
                
                return formatted;
              });
              
              const requestBody = {
                public_token,
                institution: {
                  name: String(metadata.institution?.name || 'Unknown'),
                  institution_id: String(metadata.institution?.institution_id || '')
                },
                accounts: formattedAccounts
              };
              
              log.info('[connectPlaid] Exchange request:', { 
                publicTokenLength: public_token.length,
                institution: requestBody.institution,
                accountsCount: formattedAccounts.length,
                firstAccount: formattedAccounts[0]
              });
              
              const exchangeResponse = await fetch(exchangeUrl, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
              });
              
              if (!exchangeResponse.ok) {
                const errorText = await exchangeResponse.text();
                log.error('[connectPlaid] Exchange failed:', { 
                  status: exchangeResponse.status,
                  statusText: exchangeResponse.statusText,
                  body: errorText
                });
                
                let errorDetails = errorText;
                try {
                  const errorJson = JSON.parse(errorText);
                  errorDetails = errorJson.detail || errorJson.message || errorText;
                } catch {
                  // Keep as text
                }
                
                throw new Error(`Exchange failed (${exchangeResponse.status}): ${errorDetails}`);
              }
              
              const exchangeData = await exchangeResponse.json();
              const { success, item_id, sync_completed } = exchangeData;
              
              log.info('[connectPlaid] Connection successful:', { 
                success, 
                item_id, 
                sync_completed,
                institutionName: metadata.institution?.name 
              });
              
              // Step 4: Fix existing transactions that don't have org_id
              // (One-time migration for transactions synced before org_id support)
              try {
                const fixUrl = `${API_BASE_URL}/plaid/fix_org_ids`;
                log.info('[connectPlaid] Fixing org_ids for existing transactions:', { url: fixUrl });
                
                const fixResponse = await fetch(fixUrl, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                  }
                });
                
                if (fixResponse.ok) {
                  const fixData = await fixResponse.json();
                  log.info('[connectPlaid] Fixed org_ids:', fixData);
                } else {
                  log.warn('[connectPlaid] Could not fix org_ids (non-fatal)');
                }
              } catch (fixError) {
                log.warn('[connectPlaid] Failed to fix org_ids (non-fatal):', fixError);
              }
              
              // Step 5: Wait a moment for sync to complete, then check status
              if (autoSync && sync_completed) {
                log.info('[connectPlaid] Waiting for sync to complete...');
                await new Promise(resolve => setTimeout(resolve, 2000));
                
                // Check sync status (need database item ID, not Plaid item_id)
                // The backend should return the database _id, let's check what we got
                log.info('[connectPlaid] Checking sync status for item:', item_id);
                
                try {
                  const statusResponse = await fetch(
                    `${API_BASE_URL}/plaid/sync_status?item_id=${item_id}`,
                    {
                      headers: { 'Authorization': `Bearer ${authToken}` }
                    }
                  );
                  
                  if (statusResponse.ok) {
                    const status = await statusResponse.json();
                    
                    log.info('[connectPlaid] Sync status:', status);
                    
                    resolve(createSuccessResult(
                      `✅ Plaid connected successfully! Synced ${status.entity_count} transactions.`,
                      {
                        integration: 'plaid',
                        status: 'connected',
                        itemId: item_id,
                        syncCompleted: true,
                        transactionCount: status.entity_count,
                        dataAvailable: status.has_data,
                        dateRange: status.date_range,
                        message: 'Bank transactions are now available as BankTransaction entities. Use river.entity.query({ entityType: "BankTransaction" }) to access them.'
                      },
                      {
                        format: 'json',
                        metadata: {
                          affectedEntities: [item_id]
                        } as any
                      }
                    ));
                  } else {
                    // Sync status check failed, but connection succeeded
                    log.warn('[connectPlaid] Could not check sync status, but connection succeeded');
                    resolve(createSuccessResult(
                      '✅ Plaid connected successfully! Transactions are being synced.',
                      {
                        integration: 'plaid',
                        status: 'connected',
                        itemId: item_id,
                        syncCompleted: sync_completed,
                        message: 'Bank account connected. Transactions are being synced in the background.'
                      },
                      {
                        format: 'json',
                        metadata: {
                          affectedEntities: [item_id]
                        } as any
                      }
                    ));
                  }
                } catch (statusError) {
                  // Sync status failed, but connection succeeded
                  log.warn('[connectPlaid] Sync status check failed:', statusError);
                  resolve(createSuccessResult(
                    '✅ Plaid connected successfully!',
                    {
                      integration: 'plaid',
                      status: 'connected',
                      itemId: item_id,
                      message: 'Bank account connected. Transactions are being synced in the background.'
                    },
                    {
                      format: 'json',
                      metadata: {
                        affectedEntities: [item_id]
                      } as any
                    }
                  ));
                }
              } else {
                resolve(createSuccessResult(
                  '✅ Plaid connected successfully!',
                  {
                    integration: 'plaid',
                    status: 'connected',
                    itemId: item_id,
                    message: 'Bank account connected. Transactions are being synced in the background.'
                  },
                  {
                    format: 'json',
                    metadata: {
                      affectedEntities: [item_id]
                    } as any
                  }
                ));
              }
            } catch (error: any) {
              log.error('[connectPlaid] Exchange failed:', error);
              resolve(createErrorResult(
                'Failed to complete Plaid connection',
                error.message,
                'execution',
                {
                  error: {
                    code: 'EXCHANGE_FAILED',
                    category: 'execution',
                    details: error.message,
                    retryable: true
                  }
                }
              ));
            }
          },
          onExit: (err: any) => {
            if (err) {
              log.error('[connectPlaid] User exited with error:', err);
              resolve(createErrorResult(
                'Plaid connection cancelled',
                err.display_message || err.error_message || 'User cancelled the connection',
                'execution',
                {
                  error: {
                    code: 'USER_CANCELLED',
                    category: 'execution',
                    details: err,
                    retryable: true
                  }
                }
              ));
            } else {
              log.info('[connectPlaid] User cancelled');
              resolve(createErrorResult(
                'Plaid connection cancelled',
                'User cancelled the connection',
                'execution',
                {
                  error: {
                    code: 'USER_CANCELLED',
                    category: 'execution',
                    details: 'User closed Plaid popup',
                    retryable: true
                  }
                }
              ));
            }
          }
        });
        
        try {
          linkHandler.open();
          log.info('[connectPlaid] Plaid Link opened successfully');
        } catch (openError: any) {
          log.error('[connectPlaid] Failed to open Plaid Link:', openError);
          resolve(createErrorResult(
            'Failed to open Plaid Link',
            openError.message || 'Could not open Plaid authorization popup',
            'execution'
          ));
        }
      };
      
      document.body.appendChild(script);
    });
    
  } catch (error: any) {
    log.error('[connectPlaid] Error:', error);
    return createErrorResult(
      'Failed to connect Plaid',
      error.message,
      'execution'
    );
  }
}

/**
 * Gmail Connection (placeholder - to be implemented)
 */
async function connectGmail(context: CommandExecutionContext): Promise<StandardToolResult> {
  return createErrorResult(
    'Gmail connection not yet implemented',
    'Gmail integration is planned but not yet available. It will provide access to email data.',
    'validation'
  );
}

/**
 * Google Calendar Connection (placeholder)
 */
async function connectGoogleCalendar(context: CommandExecutionContext): Promise<StandardToolResult> {
  return createErrorResult(
    'Google Calendar connection not yet implemented',
    'Google Calendar integration is planned but not yet available.',
    'validation'
  );
}

/**
 * Google Drive Connection (placeholder)
 */
async function connectGoogleDrive(context: CommandExecutionContext): Promise<StandardToolResult> {
  return createErrorResult(
    'Google Drive connection not yet implemented',
    'Google Drive integration is planned but not yet available.',
    'validation'
  );
}

/**
 * Dropbox Connection (placeholder)
 */
async function connectDropbox(context: CommandExecutionContext): Promise<StandardToolResult> {
  return createErrorResult(
    'Dropbox connection not yet implemented',
    'Dropbox integration is planned but not yet available.',
    'validation'
  );
}

export const integrationCommands = [
  connectIntegrationCommand,
  setupIntegrationCommand
];
